import { formatINR } from "@templeos/shared";
import { Alert, Button, EmptyState, MoneyField, TextField, useToast } from "@templeos/ui";
import { useState } from "react";
import { changeQty, CartError, openSlots, istMinutes, setSlot, type Cart, type CartLine } from "../lib/cart.js";
import { changeDue, type PayChoice } from "../lib/payments.js";
import { payerErrors } from "../lib/rules.js";
import { useSession } from "../session.js";
import type { Bill } from "./checkout.js";
import { QrImage } from "./PrintArea.js";

const MODES: { id: PayChoice; label: string; key: string }[] = [
  { id: "cash", label: "Cash", key: "F2" }, { id: "upi", label: "UPI QR", key: "F3" }, { id: "split", label: "Split", key: "F4" },
];
const NOTES = [10_000, 20_000, 50_000, 200_000]; // Rs 100, 200, 500, 2000: one-touch "cash received" buttons

function Line({ line, onQty, onSlot, max }: { line: CartLine; onQty: (d: number) => void; onSlot: (slot: string) => void; max: number | null }) {
  const slots = line.serviceDate ? openSlots(line.slots, istMinutes()) : [];
  return (
    <div className="pos-line">
      <div>
        <div className="pos-line-name">{line.name}</div>
        <div className="pos-line-sub">{formatINR(line.unitPrice)} each{line.slot && slots.length <= 1 ? ` · ${line.slot}` : ""}</div>
        {slots.length > 1 && (
          <label className="pos-line-sub">Slot{" "}
            <select className="tos-select" style={{ minHeight: 36, width: "auto", fontSize: 14 }} value={line.slot ?? ""} onChange={(e) => onSlot(e.target.value)}>
              {slots.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
      </div>
      <div style={{ textAlign: "right" }}>
        <div className="pos-qty">
          <button type="button" aria-label={`One less ${line.name}`} onClick={() => onQty(-1)}>−</button>
          <output aria-label={`Quantity of ${line.name}`}>{line.qty}</output>
          <button type="button" aria-label={`One more ${line.name}`} disabled={max !== null && max <= 0} onClick={() => onQty(+1)}>+</button>
        </div>
        <div className="tos-num" style={{ fontWeight: 800, marginTop: 4 }}>{formatINR(line.unitPrice * line.qty)}</div>
      </div>
    </div>
  );
}

interface Props {
  bill: Bill;
  /** Units still addable per catalogue item (prasad stock); null when unlimited or unknown. */
  roomFor?: (line: CartLine) => number | null;
  askPayer?: boolean;
}

/** The bill: lines with + / −, devotee details, payment choice (cash, UPI QR, split) and the single Pay button. */
export function BillPanel({ bill, roomFor, askPayer = true }: Props) {
  const toast = useToast();
  const { limits } = useSession();
  const [showPayer, setShowPayer] = useState(false);
  const { cart, setCart, pay, total, donation, upi, upiError, payer, setPayer } = bill;
  const edit = (fn: (c: Cart) => Cart) => {
    try { setCart(fn(cart)); } catch (e) { if (e instanceof CartError) toast(e.message, "danger"); else throw e; }
  };
  const cashPart = pay.choice === "cash" ? total : pay.choice === "split" ? pay.cashPart ?? 0 : 0;
  let change: string | null = null;
  try {
    const c = cashPart > 0 ? changeDue(cashPart, pay.tendered) : null;
    change = c === null ? null : `Change to return: ${formatINR(c)}`;
  } catch (e) {
    change = (e as Error).message;
  }
  // The same rule the Pay button, F5 and the server apply; PAN is asked for as soon as the donations need it.
  const errors = payerErrors(payer, donation, limits);
  const panNeeded = donation > limits.panAbove;
  const payerOpen = showPayer || panNeeded;

  return (
    <aside className="pos-right" aria-label="Current bill">
      <div className="pos-cart">
        {!cart.length ? <EmptyState title="No items yet">Tap a tile or scan a barcode to start the bill.</EmptyState>
          : cart.map((l) => (
            <Line key={l.key} line={l} max={roomFor?.(l) ?? null} onQty={(d) => edit((c) => changeQty(c, l.key, d))} onSlot={(slot) => edit((c) => setSlot(c, l.key, slot))} />
          ))}
      </div>
      <div className="pos-cart-foot">
        {askPayer && (
          <div>
            <Button size="sm" variant="ghost" aria-expanded={payerOpen} disabled={panNeeded} onClick={() => setShowPayer((v) => !v)}>
              {panNeeded ? "Devotee details (PAN required)" : showPayer ? "Hide devotee details" : "Devotee details (optional)"}
            </Button>
            {payerOpen && (
              <div className="tos-form-grid" style={{ marginTop: 8 }}>
                <TextField label="Name" value={payer.name ?? ""} maxLength={160} onChange={(e) => setPayer({ ...payer, name: e.target.value })} />
                <TextField label="Mobile" inputMode="tel" value={payer.phone ?? ""} maxLength={15} error={errors.phone}
                  onChange={(e) => setPayer({ ...payer, phone: e.target.value })} />
                {(donation > 0 || payer.pan) && (
                  <TextField label="PAN" required={panNeeded} value={payer.pan ?? ""} maxLength={10} error={errors.pan}
                    hint={panNeeded ? `Required for donations above ${formatINR(limits.panAbove, { decimals: false })}` : undefined}
                    onChange={(e) => setPayer({ ...payer, pan: e.target.value.toUpperCase() })} />
                )}
              </div>
            )}
          </div>
        )}
        <div className="pos-total"><span>Total</span><strong>{formatINR(total)}</strong></div>
        {total > 0 && (
          <>
            <div className="pos-modes" role="radiogroup" aria-label="Payment mode">
              {MODES.map((m) => (
                <button key={m.id} type="button" role="radio" aria-checked={pay.choice === m.id} className="pos-mode" onClick={() => pay.setChoice(m.id)}>
                  {m.label}<kbd>{m.key}</kbd>
                </button>
              ))}
            </div>
            <div className="tos-form-grid" key={pay.formKey}>
              {pay.choice === "split" && (
                <MoneyField label="Cash part" required value={pay.cashPart} onChange={pay.setCashPart}
                  hint={pay.cashPart !== null && pay.cashPart > 0 && pay.cashPart < total ? `UPI: ${formatINR(total - pay.cashPart)}` : "Rest is collected on UPI"} />
              )}
              {pay.choice !== "upi" && <MoneyField label="Cash received" value={pay.tendered} onChange={pay.setTendered} hint="Optional, to work out change" />}
            </div>
            {cashPart > 0 && (
              <div className="tos-row" role="group" aria-label="Cash received">
                <Button size="sm" onClick={() => pay.quickTender(cashPart)}>Exact</Button>
                {NOTES.filter((n) => n > cashPart).slice(0, 3).map((n) => <Button key={n} size="sm" onClick={() => pay.quickTender(n)}>{formatINR(n, { decimals: false })}</Button>)}
              </div>
            )}
            {change && <div role="status" style={{ fontWeight: 700 }}>{change}</div>}
            {upi && (
              <div className="pos-qr">
                <QrImage value={upi.link} alt={`UPI QR code for ${formatINR(upi.amount)}`} size={150} />
                <div><strong>Scan to pay {formatINR(upi.amount)}</strong><div className="tos-muted">Ref {pay.upiRef}. Print after the UPI success sound.</div></div>
              </div>
            )}
            {upiError && <Alert tone="warning">{upiError}</Alert>}
          </>
        )}
        <Button variant="primary" block className="pos-pay" loading={bill.busy} disabled={!cart.length || Boolean(errors.phone ?? errors.pan)} onClick={() => void bill.submit()}>
          {total === 0 && cart.length ? "Issue free pass" : `Pay ${formatINR(total)} & print`} <kbd style={{ fontSize: 13, opacity: 0.8 }}>F5</kbd>
        </Button>
      </div>
    </aside>
  );
}
